import React, { useCallback, useEffect, useState } from 'react';
import PowerIcon from '../../../assets/images/tutorials/powerButton.svg';
import GraphIcon from '../../../assets/images/tutorials/graphIcon.svg';
import HeartIcon from '../../../assets/images/tutorials/heartIcon.svg';
import VideoIcon from '../../../assets/images/tutorials/videoIcon.svg';
import {
  contentApprovalStatusColors,
  convertToNameCase,
  successToast,
  tutorialsStatusColors
} from '../../../utils/helper';
import DashboardInfoCard from '../../../component/common/DashboardInfoCard';

// WorkoutCard Component
const WorkoutCard = ({ title, imageUrl, usageCount, category, onMoreClick, onCardClick }) => {
  const handleMoreClick = (e) => {
    e.stopPropagation();
    if (onMoreClick) onMoreClick();
  };

  return (
    <div 
      className="bg-white rounded-lg shadow overflow-hidden flex relative h-full cursor-pointer"
      onClick={onCardClick}
    >
      <button 
        className="absolute top-3 right-3 bg-white bg-opacity-80 rounded-full w-7 h-7 flex items-center justify-center border-none z-10 hover:bg-white"
        onClick={handleMoreClick}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="3" cy="8" r="1.5" fill="#333" />
          <circle cx="8" cy="8" r="1.5" fill="#333" />
          <circle cx="13" cy="8" r="1.5" fill="#333" />
        </svg>
      </button>
      
      {/* Left side - Image */}
      <div className="w-[116px] h-[116px] relative flex-shrink-0">
        <img 
          src={imageUrl} 
          alt={title} 
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/116x116?text=Workout';
          }}
        />
      </div>
      
      {/* Right side - Content */}
      <div className="p-4 flex-grow flex flex-col justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">{title}</h3>
          <p className="text-sm text-gray-600 mb-3">Used {usageCount.toLocaleString()} times</p>
        </div>
        <button className="bg-blue-600 text-white py-1 px-4 rounded-full text-sm font-medium self-start hover:bg-blue-700">
          {category}
        </button>
      </div>
    </div>
  );
};

// Section Component for Workouts or Workout Plans
const WorkoutSection = ({ title, workouts }) => {
  const [showAll, setShowAll] = useState(false);
  const [displayedWorkouts, setDisplayedWorkouts] = useState([]);
  
  useEffect(() => {
    // Initially show only 6 workouts or all if less than 6
    setDisplayedWorkouts(workouts.slice(0, 6));
  }, [workouts]);

  const handleCardClick = (workout) => {
    console.log("Workout clicked:", workout);
  };
  
  const handleMoreClick = (workout) => {
    console.log("More options clicked for:", workout);
  };

  const handleViewAll = (e) => {
    e.preventDefault();
    if (showAll) {
      // If already showing all, switch back to showing only 6
      setDisplayedWorkouts(workouts.slice(0, 6));
    } else {
      // Show all workouts
      setDisplayedWorkouts(workouts);
    }
    setShowAll(!showAll);
  };

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">{title}</h2>
        <a 
          href="#" 
          className="text-blue-600 font-medium"
          onClick={handleViewAll}
        >
          {showAll ? "Show Less" : "View All"}
        </a>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayedWorkouts.map((workout) => (
          <WorkoutCard
            key={workout.id}
            title={workout.title}
            imageUrl={workout.imageUrl || `https://source.unsplash.com/random/116x116/?fitness,${workout.id}`}
            usageCount={workout.usageCount}
            category={workout.category}
            onCardClick={() => handleCardClick(workout)}
            onMoreClick={() => handleMoreClick(workout)}
          />
        ))}
      </div>
    </div>
  );
};

// CircularProgress component that renders the colored circular progress
const CircularProgress = ({ value, color, size = 120, strokeWidth = 8 }) => {
  // Calculate the parameters for the SVG
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = value > 100 ? 100 : value < 0 ? 0 : value;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  
  // Center position
  const center = size / 2;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Background circle */}
      <svg width={size} height={size} className="absolute">
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#f0f0f0"
          strokeWidth={strokeWidth}
        />
      </svg>
      
      {/* Progress circle */}
      <svg width={size} height={size} className="absolute" style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      
      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-sm text-gray-600">Total</div>
        <div className="text-2xl font-bold">{value}</div>
      </div>
    </div>
  );
};

// StatItem component that renders a single stat with label and value
const StatItem = ({ color, label, value, showUnit = false, unit = "" }) => {
  return (
    <div className="flex items-center gap-2 mb-2">
      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
      <div className="text-sm">
        {label}: <span className="font-bold">{value}{showUnit && unit}</span>
      </div>
    </div>
  );
};

// FitnessStatsCard component that renders the entire card
const FitnessStatsCard = ({
  title,
  stats = [],
  total = 25,
  progressColor = "#4CD5C5",
  onMoreClick
}) => {
  const handleMoreClick = (e) => {
    e.stopPropagation();
    if (onMoreClick) onMoreClick();
  };
  
  return (
    <div className="bg-white rounded-lg shadow p-4 relative">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <button 
          className="w-7 h-7 flex items-center justify-center"
          onClick={handleMoreClick}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="3" cy="8" r="1.5" fill="#333" />
            <circle cx="8" cy="8" r="1.5" fill="#333" />
            <circle cx="13" cy="8" r="1.5" fill="#333" />
          </svg>
        </button>
      </div>
      
      <div className="flex flex-col items-center">
        <CircularProgress value={total} color={progressColor} />
        
        <div className="w-full mt-4">
          {stats.map((stat, index) => (
            <StatItem
              key={index}
              color={stat.color}
              label={stat.label}
              value={stat.value}
              showUnit={stat.showUnit}
              unit={stat.unit}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const FitnessDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  
  const overviewData = [
    {
      title: 'Total Workouts',
      value: dashboardData?.liveTutorials || 0,
      icon: <img src={PowerIcon} alt="Power Icon" />
    },
    {
      title: 'Top Workout',
      value: (
        <>
          {dashboardData?.videoWatchCount || 0} <span className="text-[14px]">times</span>
        </>
      ),
      icon: <img src={VideoIcon} alt="Video Icon" />
    },
    {
      title: 'Average Workout Time',
      value: convertToNameCase(dashboardData?.mostPopular) || '-',
      icon: <img src={HeartIcon} alt="Heart Icon" />
    },
    {
      title: 'Engagement Rate',
      value: convertToNameCase(dashboardData?.leastUsed) || '-',
      icon: <img src={GraphIcon} alt="Graph Icon" />
    }
  ];

  // Sample workout data with additional items to demonstrate "View All"
  const workouts = [
    {
      id: 1,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,workout,1",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 2,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,workout,2",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 3,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,workout,3",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 4,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,workout,4",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 5,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,workout,5",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 6,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,workout,6",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 7,
      title: "15-min HIIT Challenge",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,workout,7",
      usageCount: 1245,
      category: "Cardio"
    },
    {
      id: 8,
      title: "30-min Full Body Strength",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,workout,8",
      usageCount: 1122,
      category: "Strength"
    },
    {
      id: 9,
      title: "20-min Core Crusher",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,workout,9",
      usageCount: 1550,
      category: "Core"
    }
  ];

  const workoutPlans = [
    {
      id: 1,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,plan,1",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 2,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,plan,2",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 3,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,plan,3",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 4,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,plan,4",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 5,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,plan,5",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 6,
      title: "10-min Body Weight Burn",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,plan,6",
      usageCount: 1345,
      category: "Weight Loss"
    },
    {
      id: 7,
      title: "4-Week Muscle Builder",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,plan,7",
      usageCount: 1287,
      category: "Strength"
    },
    {
      id: 8,
      title: "8-Week Marathon Prep",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,plan,8",
      usageCount: 1104,
      category: "Endurance"
    },
    {
      id: 9,
      title: "30-Day Fat Burn Challenge",
      imageUrl: "https://source.unsplash.com/random/116x116/?fitness,plan,9",
      usageCount: 1642,
      category: "Weight Loss"
    }
  ];
  
  // Sample fitness stats data
  const fitnessStats = [
    {
      title: "Fitness Goal",
      total: 25,
      progressColor: "#4CD5C5",
      stats: [
        { color: "#4CD5C5", label: "Fat Loss", value: "65%"},
        { color: "#4CD5C5", label: "Gain Muscle", value: "35%"}
      ]
    },
    {
      title: "Top Plans",
      total: 25,
      progressColor: "#8BC34A",
      stats: [
        { color: "#8BC34A", label: "Lose Weight", value: "65%"},
        { color: "#8BC34A", label: "Gain weight", value: "35%"}
      ]
    },
    {
      title: "Nutrition Preferences",
      total: 25,
      progressColor: "#FF9966",
      stats: [
        { color: "#FF9966", label: "Vegetarian", value: "65%"},
        { color: "#FF9966", label: "Gluten-free", value: "35%"}
      ]
    },
    {
      title: "Activity Levels",
      total: 25,
      progressColor: "#FF6B9A",
      stats: [
        { color: "#FF6B9A", label: "Daily", value: "15%"},
        { color: "#FF6B9A", label: "Weekly", value: "35%"}
      ]
    },
    {
      title: "Average Weight",
      total: 25,
      progressColor: "#FFD54F",
      stats: [
        { color: "#FFD54F", label: "Normal", value: "85", showUnit: true, unit: "kg"},
        { color: "#FFD54F", label: "Target", value: "79", showUnit: true, unit: "kg"}
      ]
    }
  ];
  
  const handleStatCardMoreClick = (statCard) => {
    console.log("More options clicked for stat card:", statCard);
  };
  
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-[26px]">
        {overviewData.map((item) => {
          return (
            <DashboardInfoCard
              key={item.title}
              icon={item.icon}
              title={item.title}
              value={item.value}
            />
          );
        })}
      </div>
      
      {/* Added Workout sections */}
      <div className="p-4">
        <WorkoutSection title="Workouts" workouts={workouts} />
        <WorkoutSection title="Workout Plans" workouts={workoutPlans} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 p-4">
        {fitnessStats.map((stat, index) => (
          <FitnessStatsCard
            key={index}
            title={stat.title}
            total={stat.total}
            progressColor={stat.progressColor}
            stats={stat.stats}
            onMoreClick={() => handleStatCardMoreClick(stat)}
          />
        ))}
      </div>
    </>
  );
};

export default FitnessDashboard;